import React, { useMemo, useState } from 'react'
import {
  HiPhone,
  HiUser,
  HiBriefcase,
  HiCode,
  HiGlobe,
  HiLocationMarker,
  HiExternalLink,
} from 'react-icons/hi'
import { FaWikipediaW } from 'react-icons/fa'
import { parseCrawlReport } from '../utils/crawlReport'
import { buildGooglePlacesUrl } from '../utils/mapbox'
import CheckboxTreeView from './CheckboxTreeView'
import AuthorityBoundaryMap from './AuthorityBoundaryMap'

const ContactField = ({ icon: Icon, label, value, hrefPrefix }) => {
  if (!value) return null

  const content = hrefPrefix ? (
    <a
      href={`${hrefPrefix}${value}`}
      className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 break-all"
    >
      {value}
    </a>
  ) : (
    <span className="text-gray-900 dark:text-white break-words">{value}</span>
  )

  return (
    <div className="flex items-start gap-3 text-sm">
      <Icon className="h-4 w-4 mt-0.5 shrink-0 text-gray-400" aria-hidden="true" />
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
        <div className="mt-0.5">{content}</div>
      </div>
    </div>
  )
}

const ProfileField = ({ icon: Icon, label, children }) => {
  if (!children) return null

  return (
    <div className="flex items-start gap-3 text-sm">
      <Icon className="h-4 w-4 mt-0.5 shrink-0 text-gray-400" aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
        <div className="mt-0.5">{children}</div>
      </div>
    </div>
  )
}

const GeneralContactCard = ({ contact, index, total }) => (
  <div className="space-y-3">
    {total > 1 && (
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Contact {index + 1}
      </p>
    )}
    <ContactField icon={HiUser} label="Name" value={contact.name} />
    <ContactField icon={HiBriefcase} label="Title" value={contact.title} />
    <ContactField icon={HiPhone} label="Phone" value={contact.phone} hrefPrefix="tel:" />
  </div>
)

const DepartmentLabel = ({ department }) => (
  <div className="min-w-0">
    <div className="font-medium text-gray-900 dark:text-white leading-snug">
      {department.name}
    </div>
    {(department.category || department.email || department.phone) && (
      <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
        {department.category && <span>{department.category}</span>}
        {department.email && (
          <a
            href={`mailto:${department.email}`}
            className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 break-all"
            onClick={(e) => e.stopPropagation()}
          >
            {department.email}
          </a>
        )}
        {department.phone && (
          <a
            href={`tel:${department.phone}`}
            className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            onClick={(e) => e.stopPropagation()}
          >
            {department.phone}
          </a>
        )}
      </div>
    )}
  </div>
)

const CONTACT_COLS =
  'w-full min-w-0 md:grid md:grid-cols-[3ch_minmax(0,1.2fr)_minmax(0,1fr)_12ch] md:items-baseline md:gap-x-3'

const ContactRowLabel = ({ index, contact }) => (
  <div className={`flex flex-col gap-y-1 ${CONTACT_COLS} text-sm`}>
    <div className="flex md:block gap-2 min-w-0">
      <span className="md:hidden shrink-0 w-14 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
        #
      </span>
      <span className="tabular-nums text-gray-500 dark:text-gray-400 whitespace-nowrap">
        {index}
      </span>
    </div>
    <div className="flex md:block gap-2 min-w-0">
      <span className="md:hidden shrink-0 w-14 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Email
      </span>
      <span className="min-w-0 md:truncate">
        {contact.email ? (
          <a
            href={`mailto:${contact.email}`}
            className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 break-all md:break-normal"
            onClick={(e) => e.stopPropagation()}
            title={contact.email}
          >
            {contact.email}
          </a>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">—</span>
        )}
      </span>
    </div>
    <div className="flex md:block gap-2 min-w-0">
      <span className="md:hidden shrink-0 w-14 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Name
      </span>
      <span className="min-w-0 md:truncate text-gray-900 dark:text-gray-100">
        {contact.name || <span className="text-gray-400 dark:text-gray-500">—</span>}
      </span>
    </div>
    <div className="flex md:block gap-2 min-w-0">
      <span className="md:hidden shrink-0 w-14 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Phone
      </span>
      <span className="min-w-0 md:truncate whitespace-nowrap">
        {contact.phone ? (
          <a
            href={`tel:${contact.phone}`}
            className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            onClick={(e) => e.stopPropagation()}
          >
            {contact.phone}
          </a>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">—</span>
        )}
      </span>
    </div>
  </div>
)

const slugForTreeId = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'unknown'

const buildDepartmentTree = (departments) => {
  let displayIndex = 0

  return departments.map((department, deptIndex) => ({
    id: `dept-${deptIndex}-${slugForTreeId(department.name)}`,
    label: <DepartmentLabel department={department} />,
    children: department.contacts.map((contact, contactIndex) => {
      displayIndex += 1
      const contactKey = slugForTreeId(
        contact.email || contact.name || contact.phone || `c${contactIndex}`,
      )
      return {
        id: `contact-${deptIndex}-${contactKey}`,
        label: <ContactRowLabel index={displayIndex} contact={contact} />,
      }
    }),
  }))
}

const optionHeadingClass =
  'text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3 break-words'

const ROUTING_DIAGRAM_ALT =
  'CitySeeCityFix customized pre-routing flow diagram'

const ROUTING_DIAGRAM_DESCRIPTION =
  'Flowchart with three steps: a resident submits an issue report in the app; ' +
  'CitySeeCityFix routes the report to the correct city department using the city\'s ' +
  'department directory; the routed report is delivered to staff by email or through ' +
  'an API ticket integration with the city\'s official system of record.'

const CrawlReportView = ({ report, domain = '' }) => {
  const { parsed, departmentTree } = useMemo(() => {
    const nextParsed = parseCrawlReport(report, domain)
    return {
      parsed: nextParsed,
      departmentTree: buildDepartmentTree(nextParsed.departments),
    }
  }, [report, domain])

  const {
    latitude,
    longitude,
    boundaryGeoJson,
    googlePlacesId,
    websites,
    wikipediaUrl,
    websiteVendor,
    submitReportEmail,
    generalContacts,
    departments,
  } = parsed

  const hasCoordinates = latitude != null && longitude != null
  const hasMap = hasCoordinates || Boolean(boundaryGeoJson)
  const hasWebsites = websites.length > 0 || Boolean(wikipediaUrl)
  const hasCityProfile =
    hasMap ||
    Boolean(googlePlacesId) ||
    hasWebsites ||
    Boolean(websiteVendor)
  const hasGeneralContacts = generalContacts.length > 0
  const hasDepartments = departments.length > 0
  const [showRawJson, setShowRawJson] = useState(false)

  if (!submitReportEmail && !hasCityProfile && !hasGeneralContacts && !hasDepartments) {
    return (
      <div className="px-6 py-10 text-center">
        <p className="text-gray-600 dark:text-gray-400">
          No contact details were discovered during the crawl yet.
        </p>
      </div>
    )
  }

  return (
    <div className="divide-y divide-gray-200 dark:divide-gray-700">
      {hasCityProfile && (
        <section className="px-6 py-6">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              City profile
            </h2>
            <button
              type="button"
              onClick={() => setShowRawJson((open) => !open)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            >
              <HiCode className="h-4 w-4" aria-hidden="true" />
              {showRawJson ? 'Hide API JSON' : 'Show API JSON'}
            </button>
          </div>

          {showRawJson && (
            <pre className="mb-4 max-h-80 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 p-3 text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-all">
              {JSON.stringify(report, null, 2)}
            </pre>
          )}

          <div className="flex flex-col md:flex-row gap-6">
            {hasMap && (
              <AuthorityBoundaryMap
                latitude={latitude}
                longitude={longitude}
                boundaryGeoJson={boundaryGeoJson}
              />
            )}

            <div className="flex-1 space-y-4 min-w-0">
              {hasCoordinates && (
                <ProfileField icon={HiLocationMarker} label="Coordinates">
                  <span className="tabular-nums text-gray-900 dark:text-white">
                    {latitude.toFixed(4)}, {longitude.toFixed(4)}
                  </span>
                </ProfileField>
              )}

              {googlePlacesId && (
                <ProfileField icon={HiExternalLink} label="Google Places ID">
                  <a
                    href={buildGooglePlacesUrl(googlePlacesId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 break-all"
                  >
                    {googlePlacesId}
                  </a>
                </ProfileField>
              )}

              {websiteVendor && (
                <ProfileField icon={HiGlobe} label="Website vendor">
                  <span className="text-gray-900 dark:text-white">{websiteVendor}</span>
                </ProfileField>
              )}

              {hasWebsites && (
                <ProfileField icon={HiGlobe} label="Websites">
                  <ul className="space-y-1">
                    {websites.map((url) => (
                      <li key={url}>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 break-all"
                        >
                          {url}
                        </a>
                      </li>
                    ))}
                    {wikipediaUrl && (
                      <li>
                        <a
                          href={wikipediaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                          <FaWikipediaW className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                          <span>Wikipedia</span>
                        </a>
                      </li>
                    )}
                  </ul>
                </ProfileField>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="px-6 py-6">
        {submitReportEmail ? (
          <>
            <div className="space-y-8 text-sm sm:text-base text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
              <div>
                <p className={optionHeadingClass}>
                  Option A:{' '}
                  <a
                    href={`mailto:${submitReportEmail}`}
                    className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors break-all"
                  >
                    {submitReportEmail}
                  </a>
                </p>
                <p className="mb-4">
                  This option is where we will send the reports that residents create in the free
                  app:
                </p>
                <p>
                  Your current process of staff intake of resident issues and forwarding them
                  onto the correct department or personnel would continue as it is now, but with
                  much better information. Like any report, you will be responsible for both fixing
                  the issue and also closing the loop with the resident when the issue is resolved.
                </p>
              </div>

              <div>
                <h3 className={optionHeadingClass}>Option B: Your Customized Pre-Routing</h3>
                <p className="mb-4">
                  Cities that subscribe get to dictate to the service how to set expectations,
                  handle routing, and how to close the loop with the citizen reporter!
                </p>
                <figure className="flex flex-col items-center py-2">
                  <img
                    src="/cscf_routing_flow_diagram.webp"
                    alt={ROUTING_DIAGRAM_ALT}
                    aria-describedby="routing-flow-diagram-desc"
                    className="max-w-full h-auto rounded-lg"
                  />
                  <figcaption id="routing-flow-diagram-desc" className="sr-only">
                    {ROUTING_DIAGRAM_DESCRIPTION}
                  </figcaption>
                </figure>
                <p className="mt-4">
                  We&apos;ll set expectations with the user of where to follow progress: in the
                  app or who will reach out and how. We will do it 24/7 even when the correct office
                  is closed. When your staff completes the job we can notify the user directly in
                  the app, by email, or do nothing and let your current process play out.
                </p>
              </div>

              <p>
                Pricing is based on city size and volume to keep the tax payer cost as low as
                possible.
              </p>
            </div>
            <div className="text-center">
              <button
                type="button"
                disabled
                className="inline-flex items-center justify-center rounded-lg bg-primary-600 px-6 py-3 text-base font-semibold text-white opacity-50 cursor-not-allowed"
              >
                Sign up now!
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Submit email
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No catch-all submit email was identified.
            </p>
          </>
        )}
      </section>

      {hasGeneralContacts && (
        <section className="px-6 py-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            General contacts
          </h2>
          <div className="space-y-6">
            {generalContacts.map((contact, index) => (
              <GeneralContactCard
                key={`general-${index}`}
                contact={contact}
                index={index}
                total={generalContacts.length}
              />
            ))}
          </div>
        </section>
      )}

      {hasDepartments && (
        <section className="px-6 py-6">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Departments
            </h2>
            {!hasCityProfile && (
              <button
                type="button"
                onClick={() => setShowRawJson((open) => !open)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              >
                <HiCode className="h-4 w-4" aria-hidden="true" />
                {showRawJson ? 'Hide API JSON' : 'Show API JSON'}
              </button>
            )}
          </div>

          {!hasCityProfile && showRawJson && (
            <pre className="mb-4 max-h-80 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 p-3 text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-all">
              {JSON.stringify(report, null, 2)}
            </pre>
          )}

          <div className={`mb-2 hidden ${CONTACT_COLS} text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 pl-[4.5rem]`}>
            <span>#</span>
            <span className="min-w-0">Email</span>
            <span className="min-w-0">Name</span>
            <span className="min-w-0">Phone</span>
          </div>

          <CheckboxTreeView nodes={departmentTree} defaultExpanded={[]} defaultChecked={[]} />
        </section>
      )}
    </div>
  )
}

export default CrawlReportView
